import { ref, shallowRef, computed, watch } from 'vue';
import { useComponentI18n } from '@rdrive/i18n';
import { useFilterContextRefresh } from '@rdrive/data-filters';
import { useChartTheme } from '@rdrive/theme';
import { downloadChartAsPNG } from '@rdrive/helper-chart-export';
import messages from './messages/index.js';
import { formatTimeValue } from '../../helpers/formatTimeValue.js';
import { adjustColorShade } from '../../helpers/adjustColorShade.js';

// Paging and sizing — issue-analysis's BoxPlot.vue constants, verbatim
const PAGE_SIZE = 20;
const BOX_HEIGHT = 25;
const BOX_GAP = 8;
const AXIS_MARGIN = 120;
const MIN_CHART_HEIGHT = 100;
const ANIMATION_THRESHOLD = 30;

// The tooltip of one box. The markup is the old SFC's; its colours are theme tokens, its labels
// this component's messages, and the five statistics come from the ROW — ECharts hands a boxplot
// formatter `params.value` = [dataIndex, min, Q1, median, Q3, max], so the old destructuring of
// `params.value` showed every statistic one place off (its "Min" was the row index).
const tooltipHtml = (name, row, c, t) => `
              <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.4;">
                <div style="font-weight: 600; font-size: 14px; margin-bottom: 8px; padding-bottom: 6px; border-bottom: 1px solid ${c.tooltipDivider};">
                  ${name}
                </div>
                <div style="margin-bottom: 8px;">
                  <div style="color: ${c.tooltipLabel}; font-size: 11px; text-transform: uppercase; font-weight: 500; margin-bottom: 4px;">${t('sampleSize')}</div>
                  <div style="font-weight: 500;">${row ? row.count : 'N/A'}</div>
                </div>
                <div style="margin-bottom: 8px;">
                  <div style="color: ${c.tooltipLabel}; font-size: 11px; text-transform: uppercase; font-weight: 500; margin-bottom: 4px;">${t('distribution')}</div>
                  <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; font-size: 12px;">
                    <div><span style="color: ${c.tooltipLabel};">${t('max')}:</span> <strong>${formatTimeValue(row?.max)}</strong></div>
                    <div><span style="color: ${c.tooltipLabel};">${t('q3')}:</span> <strong>${formatTimeValue(row?.Q3)}</strong></div>
                    <div><span style="color: ${c.tooltipLabel};">${t('median')}:</span> <strong style="color: ${c.tooltipMedian};">${formatTimeValue(row?.median)}</strong></div>
                    <div><span style="color: ${c.tooltipLabel};">${t('q1')}:</span> <strong>${formatTimeValue(row?.Q1)}</strong></div>
                    <div><span style="color: ${c.tooltipLabel};">${t('min')}:</span> <strong>${formatTimeValue(row?.min)}</strong></div>
                  </div>
                </div>
                <div>
                  <div style="color: ${c.tooltipLabel}; font-size: 11px; text-transform: uppercase; font-weight: 500; margin-bottom: 4px;">${t('statistics')}</div>
                  <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; font-size: 12px;">
                    <div><span style="color: ${c.tooltipLabel};">${t('average')}:</span> <strong>${formatTimeValue(row ? row.average : 'N/A')}</strong></div>
                    <div><span style="color: ${c.tooltipLabel};">${t('stdDev')}:</span> <strong>${formatTimeValue(row ? row.standard_deviation : 'N/A')}</strong></div>
                  </div>
                </div>
              </div>
            `;

export default (props) => {
  // LOCAL composer with the warn flags off (see @rdrive/i18n): the pagination, axis and tooltip
  // strings come from ./messages, while `t(navigationConfig[].titleKey)` is an APP key that
  // resolves through the root composer — the stacked bar's contract (README "navigationConfig").
  const { t } = useComponentI18n(messages);
  // THE colour source — no `chartFontColor` prop. A theme toggle repaints from the rows on
  // screen (the watch at the bottom); it never re-queries.
  const theme = useChartTheme();
  const cssVars = computed(() => ({
    '--rdrive-bp-divider': theme.value.boxPlot.paginationDivider,
    '--rdrive-bp-scrollbar': theme.value.boxPlot.scrollbarThumb,
  }));

  const chartRef = ref(null);
  // shallowRef: the option goes to ECharts as a plain object; each render assigns a new one
  const chartOption = shallowRef({});
  const chartHeight = ref(props.chartCardHeight);

  const cardStyle = computed(() => ({
    'max-height': `${props.chartCardHeight}px`,
    'min-height': '460px',
    'overflow-y': 'auto',
    'height': 'auto',
    'display': 'flex',
    'flex-direction': 'column'
  }));

  // ------------------------------------------------------------------ the drill path
  // path[0] is the top; one entry per level on screen so far: { level, title, selection }, where
  // `selection` is the row clicked to ENTER that level (null for the top and for the down arrow).
  // Empty until the first load resolves the top — no level exists before that, so nothing can
  // look one up (the old SFC started on the numeric prop default `0`, which a colour change then
  // tried to load: "No data provider found for level: 0").
  const path = ref([]);
  // The path index whose selection is this chart's filter registration right now (-1: none)
  let applied = -1;
  const started = computed(() => path.value.length > 0);

  const levelIndex = (level) => props.navigationConfig.findIndex((config) => config.level === level);
  const currentLevel = () => path.value[path.value.length - 1]?.level;
  const entry = (config, title, selection = null) => ({ level: config.level, title, selection });

  const isAtTopLevel = computed(() => !started.value || path.value.length <= 1);
  const isAtBottomLevel = computed(() => !started.value || levelIndex(currentLevel()) >= props.navigationConfig.length - 1);
  const breadcrumbs = computed(() => path.value.map((item, index) => ({
    title: item.title,
    disabled: index === path.value.length - 1,
    href: '#',
    level: item.level
  })));
  // A deep trail overflows its box: scroll to the end so the current crumb is in view (the
  // earlier crumbs stay reachable by scrolling left). Null with navigation="arrows" (v-if).
  const breadcrumbsContainer = ref(null);
  watch(breadcrumbs, () => {
    const el = breadcrumbsContainer.value;
    if (el) el.scrollLeft = el.scrollWidth;
  }, { flush: 'post' });

  // ------------------------------------------------------------------ rows and paging
  const fullDataset = shallowRef([]);
  const currentPage = ref(1);
  const totalItems = computed(() => fullDataset.value.length);
  const totalPages = computed(() => Math.ceil(totalItems.value / PAGE_SIZE));
  const startItem = computed(() => (currentPage.value - 1) * PAGE_SIZE + 1);
  const endItem = computed(() => Math.min(currentPage.value * PAGE_SIZE, totalItems.value));
  const pageRows = () => fullDataset.value.slice((currentPage.value - 1) * PAGE_SIZE, currentPage.value * PAGE_SIZE);

  const buildOption = (data) => {
    const categories = data.map((item) => item.title);
    chartHeight.value = Math.max(
      MIN_CHART_HEIGHT,
      (categories.length * BOX_HEIGHT) +
      ((categories.length - 1) * BOX_GAP) +
      AXIS_MARGIN
    );
    const shouldAnimate = categories.length <= ANIMATION_THRESHOLD;
    const { fontColor, boxPlot } = theme.value;
    const fill = props.color || boxPlot.box;
    // One formatter for the option and the series (the series' wins for a box, as before)
    const formatter = (params) => (
      params.componentSubType === 'boxplot'
        ? tooltipHtml(params.name, data[params.dataIndex], boxPlot, t)
        : ''
    );

    return {
      animation: shouldAnimate,
      animationDuration: shouldAnimate ? 800 : 0,
      tooltip: {
        trigger: 'item',
        confine: true,
        formatter
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '60px',
        top: '10px',
        containLabel: true
      },
      xAxis: {
        type: 'value',
        name: t('durationDays'),
        nameLocation: 'middle',
        nameGap: 30,
        scale: true,
        axisLabel: {
          color: fontColor,
          formatter: (value) => formatTimeValue(value)
        }
      },
      yAxis: {
        type: 'category',
        data: categories,
        inverse: true,
        axisTick: { alignWithLabel: true },
        axisLine: { show: false },
        axisLabel: {
          margin: 10,
          color: fontColor,
          fontSize: 12,
          overflow: 'truncate',
          width: 120
        }
      },
      series: [
        {
          name: 'boxplot',
          type: 'boxplot',
          data: data.map((item) => ({
            name: item.title,
            value: [item.min, item.Q1, item.median, item.Q3, item.max]
          })),
          itemStyle: {
            color: fill,
            borderColor: adjustColorShade(fill, -20, boxPlot.borderFallback)
          },
          boxWidth: [BOX_HEIGHT * 0.5, BOX_HEIGHT * 0.8],
          tooltip: { formatter }
        }
      ]
    };
  };

  const render = () => {
    chartOption.value = buildOption(pageRows());
    // The height changed with the row count: let ECharts re-measure once the DOM has it
    setTimeout(() => {
      if (chartRef.value && chartRef.value.resize) {
        try {
          chartRef.value.resize();
        } catch (error) {
          console.warn("Error resizing chart:", error);
        }
      }
    }, 200);
  };

  // New rows: sorted by median (descending; the sort is stable, so ties keep the query's order),
  // back to page 1
  const show = (rows) => {
    fullDataset.value = [...rows].sort((a, b) => b.median - a.median);
    currentPage.value = 1;
    render();
  };

  const changePage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages.value) {
      currentPage.value = newPage;
      render();
    }
  };

  // ------------------------------------------------------------------ loading
  // Every load takes a ticket; only the latest one applies its rows, so a slow load can never
  // overwrite a newer one (or a navigation made while it ran). `loads` counts the loads started:
  // after a context update, a load that the filter tick did not start is started directly.
  let ticket = 0;
  let loads = 0;
  let pending = 0;
  let armed = false; // the refresh has run once: `reloadKey` changes reload from here on

  const begin = () => { loads += 1; pending += 1; ticket += 1; return ticket; };

  // The top: navigationConfig[0], or with skipEmptyLevels the first level whose provider has
  // rows (every level empty → the first, empty)
  const renderTopLevel = async () => {
    const mine = begin();
    const config = props.navigationConfig;
    const candidates = props.skipEmptyLevels ? config.length : Math.min(1, config.length);
    let level = config[0]?.level;
    try {
      for (let i = 0; i < candidates; i++) {
        level = config[i].level;
        const dataProvider = props.dataProviders[level];
        if (!dataProvider) continue;
        const data = await dataProvider();
        if (mine !== ticket) return;
        if ((data && data.length > 0) || !props.skipEmptyLevels) {
          setTop(i);
          show(data || []);
          return;
        }
      }
      if (mine !== ticket || !config.length) return;
      setTop(0);
      show([]);
    } catch (error) {
      console.error(`[${props.title}] error loading level "${level}":`, error);
    } finally {
      pending -= 1;
    }
  };

  const setTop = (index) => {
    const config = props.navigationConfig[index];
    path.value = [entry(config, t(config.titleKey))];
    applied = -1;
  };

  const loadCurrentLevel = async () => {
    const mine = begin();
    const level = currentLevel();
    try {
      const dataProvider = props.dataProviders[level];
      if (!dataProvider) {
        console.error(`No data provider found for level: ${level}`);
        return;
      }
      const data = await dataProvider();
      if (mine === ticket) show(data || []);
    } catch (error) {
      console.error(`[${props.title}] error loading level "${level}":`, error);
    } finally {
      pending -= 1;
    }
  };

  // THE reload — a filter tick, `reloadKey`, the first load: at the top re-resolve the top,
  // drilled in reload the level on screen
  const load = () => {
    armed = true;
    return path.value.length <= 1 ? renderTopLevel() : loadCurrentLevel();
  };

  // Run a context update; its filter tick normally starts the reload (the refresh watcher runs
  // before this continuation), and when it did not tick — no filter of ours to remove, or a
  // filter that failed to apply — reload directly, so the chart always shows the path's level.
  const afterUpdate = async (update) => {
    const before = loads;
    try {
      await update?.();
    } catch (error) {
      console.error(`[${props.title}] filter update failed:`, error);
    }
    if (loads === before) await load();
  };

  // ------------------------------------------------------------------ navigation
  // Make path[index] (index ≥ 1) the level on screen again, with the filter it had: the last
  // selection at or above it, or none (the chart's registration removed). The top is a reset.
  const goTo = async (index) => {
    if (index <= 0) {
      await resetToTopLevel();
      return;
    }
    ticket += 1; // a load still in flight belongs to the old path
    path.value = path.value.slice(0, index + 1);
    let want = index;
    while (want > 0 && !path.value[want].selection) want -= 1;
    if (want === 0) want = -1; // no selection on the way: no filter of ours
    if (want === applied) {
      await loadCurrentLevel();
      return;
    }
    applied = want;
    await afterUpdate(() => (
      want > 0
        ? props.contextUpdaters[path.value[want - 1].level]?.(
          path.value[want].selection.title,
          path.value[want].selection.parentId ?? null
        )
        : props.contextUpdaters.reset?.()
    ));
  };

  const resetToTopLevel = async () => {
    ticket += 1;
    path.value = path.value.slice(0, 1);
    applied = -1;
    await afterUpdate(() => props.contextUpdaters.reset?.());
  };

  // A box click drills one level down and filters by the clicked row: the level's context
  // updater gets the row's (title, parentId) — the row itself, never a lookup by label (titles
  // repeat, e.g. one issue type under two categories). The last level is terminal: inert.
  const handleChartClick = async (params) => {
    if (!started.value || params?.componentSubType !== 'boxplot') return;
    const row = pageRows()[params.dataIndex];
    if (!row || !row.title) return;
    const index = levelIndex(currentLevel());
    if (index < 0 || index >= props.navigationConfig.length - 1) return;

    const from = currentLevel();
    ticket += 1;
    path.value = [...path.value, entry(props.navigationConfig[index + 1], row.title, row)];
    applied = path.value.length - 1;
    await afterUpdate(() => props.contextUpdaters[from]?.(row.title, row.parentId ?? null));
  };

  // navigation="arrows": down shows the next level as it is (no selection, the filter unchanged)
  const navigateDown = async () => {
    if (isAtBottomLevel.value) return;
    const next = props.navigationConfig[levelIndex(currentLevel()) + 1];
    ticket += 1;
    path.value = [...path.value, entry(next, t(next.titleKey))];
    await loadCurrentLevel();
  };

  const navigateUp = async () => {
    if (isAtTopLevel.value) return;
    await goTo(path.value.length - 2);
  };

  // navigation="breadcrumbs": a crumb is found by its text (the stacked bar's mechanism)
  const handleBreadcrumbClick = async (e) => {
    const index = path.value.findIndex((item) => item.title === e?.target?.text);
    if (index < 0 || index === path.value.length - 1) return;
    await goTo(index);
  };

  // ------------------------------------------------------------------ PNG export
  // Opt-in `exportFilename`: the title row's download button — and callable through a template
  // ref. No legend (one box series); the subtitle is the breadcrumb trail once drilled (with
  // navigation="arrows" there is no trail on screen, so none in the PNG either).
  const downloadPng = () => {
    const chart = chartRef.value?.chart;
    if (!chart || !props.exportFilename || totalItems.value === 0) return;
    const trail = path.value.map((item) => item.title);
    downloadChartAsPNG(chart, props.exportFilename, null, {
      mode: theme.value.mode,
      legend: false,
      title: props.exportTitle || props.title,
      subtitle: props.navigation === 'breadcrumbs' && trail.length > 1 ? trail.join(' › ') : '',
    });
  };

  // ------------------------------------------------------------------ watchers
  // The app's own parameters (e.g. the selected status) changed: reload like a filter tick
  watch(() => props.reloadKey, () => {
    if (armed && props.dataLoaded) load();
  });

  // A theme or colour change repaints the rows on screen — no query. A load in flight renders
  // with the new values itself.
  watch([theme, () => props.color], () => {
    if (started.value && pending === 0) render();
  });

  // `filterContext` may be an ARRAY of contexts (issue-analysis's box plot reads both of its
  // contexts): the chart then reloads on a tick from ANY of them — BaseTimeSeriesChart's
  // stand-in, verbatim: the composable deep-watches one object's `filtersLastUpdated`, so an
  // array goes in as one stand-in listing every context's tick ref, rebuilt only when the
  // contexts themselves change (an inline array literal does not reload on a re-render).
  let multiContext = null;
  const refreshContext = computed(() => {
    const ctx = props.filterContext;
    if (!Array.isArray(ctx)) return ctx;
    const ticks = ctx.map((c) => c?.filtersLastUpdated);
    const prev = multiContext?.filtersLastUpdated;
    if (!prev || prev.length !== ticks.length || ticks.some((tick, i) => tick !== prev[i])) {
      multiContext = { filtersLastUpdated: ticks };
    }
    return multiContext;
  });

  // The first load once dataLoaded is true (on mount or when it flips), then a reload on every
  // filter tick (no debounce, like the old SFC's watcher)
  useFilterContextRefresh(
    refreshContext,
    computed(() => props.dataLoaded),
    load,
    { debounce: 0, immediate: true, requireDataLoaded: true }
  );

  return {
    t,
    cssVars,
    // `chartRef` and `breadcrumbsContainer` are STRING template refs (`ref="…"` in
    // component.vue) — they only bind because they are returned under those exact names.
    chartRef,
    breadcrumbsContainer,
    chartOption,
    chartHeight,
    cardStyle,
    breadcrumbs,
    isAtTopLevel,
    isAtBottomLevel,
    currentPage,
    totalPages,
    totalItems,
    startItem,
    endItem,
    changePage,
    handleChartClick,
    handleBreadcrumbClick,
    navigateUp,
    navigateDown,
    downloadPng,
  };
};
